import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "wouter/use-browser-location";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { fetchMyConcurrency, fetchTenantConcurrency, isConcurrencyForbiddenError, isConcurrencyUnsupportedError, type ConcurrencySummary } from "@/api/usage-concurrency";
import { ConcurrencySection, type ConcurrencyLoadState } from "@/components/usage-intensity";

import {
  fetchMyCosts,
  fetchTenantCosts,
  isCostsForbiddenError,
  isCostsUnsupportedError,
  type TenantCostSummary,
  type UserCostSummary,
} from "@/api/costs";
import { useAuthWithTestMode } from "@/hooks/use-auth-with-test-mode";
import { useMe, type Me } from "@/hooks/use-me";
import { goBackToConversation } from "@/lib/in-app-history";
import type { AccessTokenGetter } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { PageScaffold } from "@/components/page-scaffold";
import { Spinner } from "@/components/ui/spinner";

type CostLoadState<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "unsupported" }
  | { status: "forbidden" }
  | { status: "error" };

export interface CostsPageProps {
  getToken: AccessTokenGetter;
  me: Me | null;
  onBack: () => void;
}

const usdFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsd(value: number): string {
  return usdFormatter.format(value);
}

function formatDay(value: string): string {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function classifyCostError(error: unknown): "unsupported" | "forbidden" | "error" {
  if (isCostsUnsupportedError(error)) return "unsupported";
  if (isCostsForbiddenError(error)) return "forbidden";
  return "error";
}

function CostFailure({
  status,
  onRetry,
}: {
  status: "unsupported" | "forbidden" | "error";
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const message = status === "unsupported"
    ? t("costs.unsupported")
    : status === "forbidden"
      ? t("costs.forbidden")
      : t("costs.loadFailed");

  return (
    <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4" role="alert">
      <p className="text-sm text-destructive">{message}</p>
      {status === "error" && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="mr-2 size-4" />
          {t("costs.retry")}
        </Button>
      )}
    </div>
  );
}

function LoadingCostState() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground" role="status">
      <Spinner className="size-4" />
      {t("costs.loading")}
    </div>
  );
}

function CostNotices({
  summary,
}: {
  summary: UserCostSummary | TenantCostSummary;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      {summary.unknownCostCount > 0 && (
        <p role="status">
          {t("costs.unknownPricing", { count: summary.unknownCostCount })}
        </p>
      )}
      {summary.unattributedCostCount > 0 && (
        <p role="status">
          {t("costs.unattributed", { count: summary.unattributedCostCount })}
        </p>
      )}
    </div>
  );
}

function DailyCostChart({ summary }: { summary: UserCostSummary | TenantCostSummary }) {
  const { t } = useTranslation();
  const maxCost = Math.max(...summary.daily.map((row) => row.costUsd), 0);
  const dailyLabel = t("costs.daily");

  return (
    <section aria-labelledby="costs-daily-heading" className="space-y-3">
      <h3 id="costs-daily-heading" className="text-sm font-medium">{dailyLabel}</h3>
      <div className="space-y-2" aria-label={dailyLabel}>
        {summary.daily.map((row) => {
          const width = maxCost > 0 ? `${Math.max((row.costUsd / maxCost) * 100, row.costUsd > 0 ? 2 : 0)}%` : "0%";
          return (
            <div key={row.date} className="grid grid-cols-[5rem_1fr_auto] items-center gap-3 text-xs">
              <span>{formatDay(row.date)}</span>
              <div className="h-2 rounded-full bg-muted" aria-hidden="true">
                <div className="h-2 rounded-full bg-primary" style={{ width }} />
              </div>
              <span className="tabular-nums">{formatUsd(row.costUsd)}</span>
            </div>
          );
        })}
      </div>
      <table className="w-full text-xs" aria-label={dailyLabel}>
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th scope="col" className="py-2">{t("costs.date")}</th>
            <th scope="col" className="py-2 text-right">{t("costs.cost")}</th>
            <th scope="col" className="py-2 text-right">{t("costs.entries")}</th>
          </tr>
        </thead>
        <tbody>
          {summary.daily.map((row) => (
            <tr key={row.date} className="border-b last:border-0">
              <th scope="row" className="py-2 text-left font-normal">{formatDay(row.date)}</th>
              <td className="py-2 text-right tabular-nums">{formatUsd(row.costUsd)}</td>
              <td className="py-2 text-right tabular-nums">{row.entryCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function CostSummarySection({
  heading,
  summary,
}: {
  heading: string;
  summary: UserCostSummary | TenantCostSummary;
}) {
  const { t } = useTranslation();
  const isEmpty = summary.daily.every((row) => row.entryCount === 0);

  return (
    <section className="space-y-5 rounded-xl border bg-card p-5 shadow-sm" aria-labelledby={`${summary.scope}-costs-heading`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${summary.scope}-costs-heading`} className="text-lg font-semibold">{heading}</h2>
          <p className="text-xs text-muted-foreground">
            {t("costs.lastDays", { count: summary.range.days })}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">{t("costs.total")}</p>
          <p className="text-2xl font-semibold tabular-nums">{formatUsd(summary.totalCostUsd)}</p>
        </div>
      </div>

      <DailyCostChart summary={summary} />
      {isEmpty && (
        <p className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">{t("costs.empty")}</p>
      )}
      <CostNotices summary={summary} />
    </section>
  );
}

function TenantCostSection({ summary }: { summary: TenantCostSummary }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-4 rounded-xl border bg-card p-5 shadow-sm" aria-labelledby="tenant-costs-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="tenant-costs-heading" className="text-lg font-semibold">{t("costs.tenantCosts")}</h2>
          <p className="text-xs text-muted-foreground">{t("costs.userCount", { count: summary.userCount })}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">{t("costs.total")}</p>
          <p className="text-2xl font-semibold tabular-nums">{formatUsd(summary.totalCostUsd)}</p>
        </div>
      </div>
      {summary.users.length === 0 ? (
        <p className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">{t("costs.noUsers")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label={t("costs.tenantCosts")}>
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th scope="col" className="py-2 pr-4">{t("costs.user")}</th>
                <th scope="col" className="py-2 pr-4 text-right">{t("costs.total")}</th>
                <th scope="col" className="py-2 pr-4 text-right">{t("costs.entries")}</th>
              </tr>
            </thead>
            <tbody>
              {summary.users.map((user) => (
                <tr key={user.userId} className="border-b last:border-0">
                  <th scope="row" className="py-2 pr-4 text-left font-normal">{user.userId}</th>
                  <td className="py-2 pr-4 text-right tabular-nums">{formatUsd(user.totalCostUsd)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{user.entryCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CostNotices summary={summary} />
    </section>
  );
}

function renderCostState<T>(
  state: CostLoadState<T>,
  ready: (data: T) => ReactNode,
  onRetry: () => void,
) {
  switch (state.status) {
    case "loading":
      return <LoadingCostState />;
    case "ready":
      return ready(state.data);
    default:
      return <CostFailure status={state.status} onRetry={onRetry} />;
  }
}

export function CostsPage({ getToken, me, onBack }: CostsPageProps) {
  const { t } = useTranslation();
  const [retryVersion, setRetryVersion] = useState(0);
  const [concurrencyState, setConcurrencyState] = useState<ConcurrencyLoadState>({ status: "loading" });
  const [ownState, setOwnState] = useState<CostLoadState<UserCostSummary>>({ status: "loading" });
  const [tenantState, setTenantState] = useState<CostLoadState<TenantCostSummary> | null>(null);
  const showTenantCosts =
    me?.is_tenant_admin === true || me?.is_platform_admin === true;

  const retry = useCallback(() => setRetryVersion((version) => version + 1), []);

  useEffect(() => {
    let cancelled = false;
    setOwnState({ status: "loading" });
    void fetchMyCosts(getToken).then(
      (data) => {
        if (!cancelled) setOwnState({ status: "ready", data });
      },
      (error: unknown) => {
        if (!cancelled) setOwnState({ status: classifyCostError(error) });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [getToken, retryVersion]);

  useEffect(() => {
    if (!showTenantCosts) {
      setTenantState(null);
      return;
    }
    let cancelled = false;
    setTenantState({ status: "loading" });
    void fetchTenantCosts(getToken).then(
      (data) => {
        if (!cancelled) setTenantState({ status: "ready", data });
      },
      (error: unknown) => {
        if (!cancelled) setTenantState({ status: classifyCostError(error) });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [getToken, retryVersion, showTenantCosts]);

  useEffect(() => {
    let cancelled = false;
    setConcurrencyState({ status: "loading" });
    void (showTenantCosts ? fetchTenantConcurrency(getToken) : fetchMyConcurrency(getToken)).then(
      (data: ConcurrencySummary) => { if (!cancelled) setConcurrencyState({ status: "ready", data }); },
      (error: unknown) => { if (!cancelled) setConcurrencyState({ status: isConcurrencyUnsupportedError(error) ? "unsupported" : isConcurrencyForbiddenError(error) ? "forbidden" : "error" }); },
    );
    return () => { cancelled = true; };
  }, [getToken, retryVersion, showTenantCosts]);

  useEffect(() => { document.title = t("usage.title"); }, [t]);

  return (
    <PageScaffold
      title={<h1 className="truncate text-sm font-semibold tracking-tight">{t("usage.title")}</h1>}
      backAriaLabel={t("common:actions.backToChat")}
      onBack={onBack}
      contentClassName="flex flex-col gap-5"
    >
      <ConcurrencySection state={concurrencyState} onRetry={retry} getToken={getToken} />
      {renderCostState(
        ownState,
        (summary) => <CostSummarySection heading={t("costs.yourCosts")} summary={summary} />,
        retry,
      )}
      {showTenantCosts && tenantState && renderCostState(
        tenantState,
        (summary) => <TenantCostSection summary={summary} />,
        retry,
      )}
    </PageScaffold>
  );
}

function AuthenticatedCostsPage({ getAccessToken }: { getAccessToken: AccessTokenGetter }) {
  const { me } = useMe(getAccessToken);

  return (
    <CostsPage
      getToken={getAccessToken}
      me={me}
      onBack={goBackToConversation}
    />
  );
}

/** Dedicated, authenticated route for cost visibility. */
export function CostsPageRoute() {
  const { t } = useTranslation();
  const { user, isLoading, getAccessToken } = useAuthWithTestMode();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-4 text-foreground">
        <div className="w-full max-w-md space-y-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/60">
            <AlertCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">{t("identity.signInRequired")}</h1>
          <Button
            onClick={() => {
              navigate("/?return_to=" + encodeURIComponent(window.location.pathname + window.location.search));
            }}
            className="w-full"
          >
            {t("identity.signIn")}
          </Button>
        </div>
      </div>
    );
  }

  return <AuthenticatedCostsPage getAccessToken={getAccessToken} />;
}
